import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../components/shared/ScrollBasic';
import {
  Color,
  ImageScaleView,
  MultiplyFilter,
} from '@play-co/timestep-core/ui';
import { Pet, PetSpecies } from 'src/replicant/buruburu/pet';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { getCreativeSuffix } from 'src/lib/utils';
import { Encyclopedia } from 'src/replicant/encyclopedia';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';

type EncyclopediaItem = {
  id: PetSpecies;
  state: 'unveiled' | 'veiled' | 'unlocked';
  notification: boolean;
};
type EncyclopediaRow = [EncyclopediaItem, EncyclopediaItem?, EncyclopediaItem?];

export default class PopupEncyclopedia extends PopupBasic {
  private scroll: ScrollBasic<EncyclopediaRow>;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      skipMessage: true,
      tracking: { feature: 'encyclopedia', $subFeature: 'encyclopedia-popup' },
      titleIcon: {
        image: 'assets/ui/encyclopedia/icon/encyclopedia_header_icon.png',
        width: 90,
        height: 80,
      },
    });

    this.buttonClose.removeFromSuperview();

    this.scroll = new ScrollBasic<EncyclopediaRow>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 45,
      margin: 15,
      rect: {
        x: (this.box.style.width - 512) * 0.5,
        y: 94,
        width: 512,
        height: 586,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'encyclopedia', $subFeature: 'encyclopedia-back' },
    });

    this.createBanner();
  }

  init() {
    super.init({
      title: i18n('encyclopedia.popup.title'),
    });

    const { user } = StateObserver.getState();
    const {
      ruleset: { species },
      getters,
    } = Encyclopedia;
    const { getSpeciesState, getSpeciesNotification } = getters(user);

    this.scroll.setItems(
      species.reduce((result, _, i, array) => {
        if (i % 3 === 0) {
          result.push(
            array.slice(i, i + 3).map((id) => ({
              id,
              state: getSpeciesState(id),
              notification: getSpeciesNotification(id),
            })),
          );
        }
        return result;
      }, []),
    );

    void StateObserver.invoke.clearEncyclopediaNotifications();
  }

  private createBanner() {
    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/encyclopedia/banner/blue_banner.png',
      width: 509,
      height: 50,
      x: this.box.style.width / 2,
      y: 120,
      centerOnOrigin: true,
    });
  }

  private createItem(superview: View, _: number, row: EncyclopediaRow) {
    const view = new FlexBoxView({
      superview,
      width: superview.style.width,
      height: 160,
      flex: {
        paddingHorizontal: 7,
      },
    });

    row.forEach(({ id, state, notification }) => {
      const frame = new ImageScaleView({
        superview: view,
        image: 'assets/ui/shop/frames/scroll_cell.png',
        scaleMethod: '9slice',
        width: 160,
        height: 160,
        x: (superview.style.width - 485) * 0.5,
        sourceSlices: {
          horizontal: { left: 55, right: 55 },
          vertical: { top: 56.5, bottom: 56.5 },
        },
      });

      const node = getFlexNode(frame);
      node.updateStyle({
        flexShrink: 0,
        marginLeft: 5,
      });

      const image = new ImageScaleView({
        superview: frame,
        width: 100,
        height: 100,
        x: frame.style.width * 0.5,
        y: frame.style.height * 0.35,
        centerOnOrigin: true,
        scaleMethod: 'contain',
        image: `assets/ui/buruburu/species/linepet_${id}.png`,
      });

      if (state !== 'unlocked') {
        image.setFilter(
          new MultiplyFilter(
            new Color(
              `rgba(255, 255, 255,  ${state === 'unveiled' ? 155 : 255})`,
            ),
          ),
        );
      }

      const petName =
        Pet.ruleset.evolutions[id]?.[`name_${getCreativeSuffix()}`];

      new ButtonScaleViewWithText({
        superview: frame,
        image: 'assets/ui/encyclopedia/frames/outline.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 15, right: 15 },
          vertical: { top: 15, bottom: 15 },
        },
        font: 'Body',
        textColor: '#9C490D',
        fontSize: 20,
        width: 140,
        height: 30,
        x: frame.style.width * 0.5,
        y: frame.style.height - 35,
        tracking: { feature: 'encyclopedia', $subFeature: 'encyclopedia-pet' },
        localeText: () => `${state === 'veiled' ? '???' : petName}`,
        centerOnOrigin: true,
      });

      if (notification) {
        const badge = new ImageScaleView({
          superview: frame,
          width: 71,
          height: 32,
          x: -1,
          y: 7,
          image: `assets/ui/encyclopedia/banner/notifiaction_ribbon.png`,
        });

        new LangBitmapFontTextView({
          superview: badge,
          x: 10,
          width: 45,
          height: badge.style.height,
          align: 'center',
          verticalAlign: 'center',
          font: bitmapFonts('Body'),
          localeText: () => 'NEW',
          size: 21,
        });
      }
    });

    return view;
  }
}
